import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * HAZ_BASIC_01 스키마
 * - fields: 폼/그리드에서 공통으로 쓰는 필드 정의
 *   - initial: 초기값
 *   - label: 필드명
 *   - required: 필수 입력 여부
 *   - requiredWhen: 언제 필수인지 조건
 *
 * - mapper: 저장/삭제 API payload 생성 규칙
 *   - mapRowsBySchema(rows, mapper, context)로 payload를 만듬
 *   - context에는 userInfo 등이 들어오며(mapRowsBySchema에 기본족으로 정의되어 있음),
 *     화면에서 추가로 필요한 값(custCd 등)을 context로 넘겨줄 수 있다.
 */
export const HAZ_BASIC_01_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    chaebun: { initial: '', label: '채번' },
    custCd: { initial: '', label: '사업장코드' },
    initialRegular: { initial: '', label: '최초/정기' },
    machineName: { initial: '', label: '기계·기구·설비명', required: 'Y' },
    type: { initial: '', label: '종류' },
    installLocation: { initial: '', label: '설치장소' },
    department: { initial: '', label: '담당부서' },
    personInCharge: { initial: '', label: '담당자' },
    mainStructureSpec: { initial: '', label: '주요구조부 규격 (형식/용량 등)' },
    manufacturer: { initial: '', label: '제조사' },
    serialNo: { initial: '', label: '제조번호' },
    initialInstallDate: { initial: '', label: '최초 설치일자' },
    inspectionCycle: { initial: '', label: '검사주기 (3년/2년)' },
    inspectionDate: { initial: '', label: '합격통보일 (검사 승인 일정일)' },
    validityDate: { initial: '', label: '유효기간' },
    passNo: { initial: '', label: '합격번호' },
    inspectionAgency: { initial: '', label: '검사기관' },
    inspector: { initial: '', label: '검사원' },
    remark: { initial: '', label: '비고' },
    alarmYn: { initial: '', label: '알람여부 (Y/N)', required: 'Y' },
    alarmId: { initial: '', label: '알람ID' },
    machineImageFileName: { initial: '', label: '기계·기구·설비 이미지 파일명' },
    machineImageSaveName: { initial: '', label: '기계·기구·설비 이미지 저장명' },
    machineImageFilePath: { initial: '', label: '기계·기구·설비 이미지 파일 경로' },
    previewFilePath: { initial: '', label: '미리보기 파일 경로' },
  },

  mapper: {
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
    },
    search2: {
      Gubun: (_, context) => Commons.EncryptAES('0') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    search3: {
      CoCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.coCd || '') || '',
      Gubun: (_, context) => Commons.EncryptAES('0') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    search4: {
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    delete1: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
    },
    save1: {
      Chaebun: (row) => {
        const chaebun = row.chaebun;
        const chaebunValue =
          chaebun && chaebun.toString().trim() !== '' ? chaebun.toString() : '0';
        return Commons.EncryptAES(chaebunValue) || '';
      },
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      InitialRegular: (row) => Commons.EncryptAES(row.initialRegular || '') || '',
      MachineName: (row) => Commons.EncryptAES(row.machineName || '') || '',
      Type: (row) => Commons.EncryptAES(row.type || '') || '',
      InstallLocation: (row) => Commons.EncryptAES(row.installLocation || '') || '',
      Department: (row) => Commons.EncryptAES(row.department || '') || '',
      PersonInCharge: (row) => Commons.EncryptAES(row.personInCharge || '') || '',
      MainStructureSpec: (row) => Commons.EncryptAES(row.mainStructureSpec || '') || '',
      Manufacturer: (row) => Commons.EncryptAES(row.manufacturer || '') || '',
      SerialNo: (row) => Commons.EncryptAES(row.serialNo || '') || '',
      InitialInstallDate: (row) => {
        if (!row.initialInstallDate || !dayjs(row.initialInstallDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.initialInstallDate).format('YYYY-MM-DD')) || '';
      },
      InspectionCycle: (row) => Commons.EncryptAES(row.inspectionCycle || '') || '',
      InspectionDate: (row) => {
        if (!row.inspectionDate || !dayjs(row.inspectionDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.inspectionDate).format('YYYY-MM-DD')) || '';
      },
      ValidityDate: (row) => {
        if (!row.validityDate || !dayjs(row.validityDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.validityDate).format('YYYY-MM-DD')) || '';
      },
      PassNo: (row) => Commons.EncryptAES(row.passNo || '') || '',
      InspectionAgency: (row) => Commons.EncryptAES(row.inspectionAgency || '') || '',
      Inspector: (row) => Commons.EncryptAES(row.inspector || '') || '',
      Remark: (row) => Commons.EncryptAES(row.remark || '') || '',
      MachineImageFileName: (row) =>
        Commons.EncryptAES(row.machineImageFileName || '') || '',
      MachineImageSaveName: (row) =>
        Commons.EncryptAES(row.machineImageSaveName || '') || '',
      MachineImageFilePath: (row, context) => {
        // 새 파일 업로드 시: previewFilePath에서 saveName 추출하여 최종 경로 생성
        const previewPath = row.previewFilePath || '';
        if (previewPath) {
          const saveName = previewPath.split('/').pop() || '';
          const finalPath = `HazardousMachines/${context.search?.custCd}/HAZ_BASIC_01/${saveName}`;
          return Commons.EncryptAES(finalPath) || '';
        }

        // 기존 파일 유지 시: machineImageFilePath 직접 사용
        const existingFilePath = row.machineImageFilePath || '';
        if (!existingFilePath) return '';
        return Commons.EncryptAES(existingFilePath) || '';
      },
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
      AlarmYn: (row) => Commons.EncryptAES(row.alarmYn || 'N') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    save2: {
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
      CalId: (row) => Commons.EncryptAES(row.calId ? row.calId.toString() : '') || '',
      ValidityDate: (row) => {
        if (!row.validityDate || !dayjs(row.validityDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.validityDate).format('YYYY-MM-DD')) || '';
      },
      AlarmYn: (row) => Commons.EncryptAES(row.alarmYn || 'N') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    save3: {
      Gubun: (_, context) => Commons.EncryptAES('0') || '',
      ToggleStatus: (row) => Commons.EncryptAES(row.toggleStatus || 'N') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
  },
};
